import React from 'react';
import _ from 'lodash';
import Avatar from '@mui/material/Avatar';

import { AudienceCard } from '../modules/audience-card';
import { CategoryDashboard } from '../modules/audience-category-dashboard';
import { isInternalUser } from 'states/profile/business-rules';
import FlexSegmentsForm from 'forms/flex-segments-form';
import flags from 'containers/flags/service';

const categoryMap = {
    flex_standard_brand: {
        label: 'Brand',
        description: 'Users who frequent locations from select brands',
        color: '#FFB0C1',
    },
    flex_location_category: {
        label: 'Location Categories',
        description: 'Users who frequent top locations from select categories',
        color: '#FFCE50',
    },
};

const subCategoryColor = {
    'Location Categories': '#FFCE50',
    Brand: '#FFB0C1',
};

const tabValueMapping = {
    all: {
        value: ['flex_standard_brand', 'flex_location_category'],
        label: 'All',
    },
    location_categories: {
        value: ['flex_location_category'],
        label: 'Location Categories',
    },
    brand: {
        value: ['flex_standard_brand'],
        label: 'Brand',
    },
};

const tabValueMappingV2 = {
    all: {
        value: ['Brand', 'Location Categories'],
        label: 'All',
    },
    location_categories: {
        value: ['Location Categories'],
        label: 'Location Categories',
    },
    brand: {
        value: ['Brand'],
        label: 'Brand',
    },
};

const getTabs = ({ audiences }) => {
    const tabs = [
        {
            value: 'all',
            label: 'All',
            avatar: <Avatar>{audiences && audiences.length}</Avatar>,
        },
        {
            value: 'location_categories',
            label: 'Location Categories',
            tooltip:
                'Audiences composed of users that have been seen within mapped polygon locations, and are best used to target based on consumer interest, customer loyalty, or conquesting strategies.',
            avatar: (
                <Avatar>
                    {audiences &&
                        _.filter(audiences, audience => {
                            if (
                                flags.isEnabled(
                                    'en_2987_use_audience_category_and_subCategory_code'
                                )
                            ) {
                                return audience.subCategory === 'Location Categories';
                            } else {
                                return audience.source_type === 'flex_location_category';
                            }
                        }).length}
                </Avatar>
            ),
        },
        {
            value: 'brand',
            label: 'Brand',
            tooltip:
                'Audiences composed of users that exhibited habitual visitation patterns, and are best used to target based on offline interests and frequent visitors to locations',
            avatar: (
                <Avatar>
                    {audiences &&
                        _.filter(audiences, audience => {
                            if (
                                flags.isEnabled(
                                    'en_2987_use_audience_category_and_subCategory_code'
                                )
                            ) {
                                return audience.subCategory === 'Brand';
                            } else {
                                return audience.source_type === 'flex_standard_brand';
                            }
                        }).length}
                </Avatar>
            ),
        },
    ];

    return tabs;
};

const sortingOptions = [
    {
        label: 'Sort by: Name',
        value: 'name',
        direction: 'asc',
    },
    {
        label: 'Sort by: Newest',
        value: 'id',
        direction: 'desc',
    },
];

const Layout = ({
    audienceSegments,
    openFormModal,
    onSave,
    onSearch,
    activeAudience,
    organizationId,
}) => {
    const tabs = getTabs({ audiences: audienceSegments });

    const linkToAudienceTab = {
        pathname: '/audiences/all',
        query: { audienceTab: 'pelmorex_data' },
        linkBackLabel: 'Pelmorex Data',
    };

    return (
        <CategoryDashboard
            audienceSegments={audienceSegments}
            onSearch={onSearch}
            segmentCount={audienceSegments ? audienceSegments.length : 0}
            createEditAction={openFormModal}
            activeAudience={activeAudience}
            organizationId={organizationId}
            sortingOptions={sortingOptions}
            description={
                'Applying a preset radius around specific points of interest, these segments identify consumers have been seen within a specific proximity to shared industry locations.'
            }
            title={'Flex Category'}
            linkToAudienceTab={linkToAudienceTab}
            bannerImagePath={'/images/audience-segments/banners/flex category-banner.png'}
            modal={<FlexSegmentsForm onSave={onSave} type="standard" />}
            tabFields={{ tabs, tabValueMapping, tabValueMappingV2, valueField: 'source_type' }}
            renderAudienceCard={segment => (
                <AudienceCard
                    onClick={
                        isInternalUser() && segment.source_type === 'flex_standard_brand'
                            ? () => openFormModal(segment.id)
                            : null
                    }
                    segment={segment}
                    displayId={true}
                    active={activeAudience === segment.id}
                    categoryLabel={
                        flags.isEnabled('en_2987_use_audience_category_and_subCategory_code')
                            ? segment.subCategory
                            : categoryMap[segment.source_type].label
                    }
                    categoryColor={
                        flags.isEnabled('en_2987_use_audience_category_and_subCategory_code')
                            ? subCategoryColor[segment.subCategory]
                            : categoryMap[segment.source_type].color
                    }
                    organizationId={organizationId}
                />
            )}
        />
    );
};

export default Layout;
